import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import planService from '../../services/planService';
import subscriptionService from '../../services/subscriptionService'; // Serviço para gerenciar assinaturas (obter todas)
// import userService from '../../services/userService'; // Se você tiver um serviço para buscar usuários (Admin)
import './AdminDashboard.css'; 

// Componente para um card de plano individual (para criar/editar)
const PlanFormCard = ({ plan = null, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    price: plan?.price || '',
    currency: plan?.currency || 'BRL',
    interval: plan?.interval || 'monthly',
    durationMonths: plan?.durationMonths || '',
    features: plan?.features.join('\n') || '', // Features como string separada por linha
    isAvailable: plan?.isAvailable ?? true,
    mpPlanId: plan?.mpPlanId || ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Converte features de volta para array
    const dataToSend = {
      ...formData,
      features: formData.features.split('\n').map(f => f.trim()).filter(Boolean),
      price: parseFloat(formData.price),
      durationMonths: parseInt(formData.durationMonths, 10)
    };
    onSave(plan?._id, dataToSend);
  };

  return (
    <div className="admin-form-card">
      <h3>{plan ? 'Editar Plano' : 'Novo Plano'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Nome do Plano</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>Descrição</label>
          <textarea name="description" value={formData.description} onChange={handleChange} rows="3" />
        </div>
        <div className="form-group">
          <label>Preço</label>
          <input type="number" name="price" value={formData.price} onChange={handleChange} required min="0" step="0.01" />
        </div>
        <div className="form-group">
          <label>Moeda</label>
          <select name="currency" value={formData.currency} onChange={handleChange}>
            <option value="BRL">BRL</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <div className="form-group">
          <label>Intervalo de Cobrança</label>
          <select name="interval" value={formData.interval} onChange={handleChange} required>
            <option value="monthly">Mensal</option>
            <option value="quarterly">Trimestral</option>
            <option value="semi-annually">Semestral</option>
            <option value="annually">Anual</option>
          </select>
        </div>
        <div className="form-group">
          <label>Duração em Meses</label>
          <input type="number" name="durationMonths" value={formData.durationMonths} onChange={handleChange} required min="1" />
        </div>
        <div className="form-group">
          <label>Features (uma por linha)</label>
          <textarea name="features" value={formData.features} onChange={handleChange} rows="5" />
        </div>
        <div className="form-group checkbox">
          <input type="checkbox" id="isAvailable" name="isAvailable" checked={formData.isAvailable} onChange={handleChange} />
          <label htmlFor="isAvailable">Disponível para novas assinaturas</label>
        </div>
        <div className="form-group">
          <label>ID do Plano MP (opcional)</label>
          <input type="text" name="mpPlanId" value={formData.mpPlanId} onChange={handleChange} placeholder="ID do Plano no Mercado Pago" />
        </div>
        <div className="form-actions">
          <button type="submit" disabled={isSaving} className="btn-primary">{isSaving ? 'Salvando...' : 'Salvar Plano'}</button>
          <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
        </div>
      </form>
    </div>
  );
};


function AdminDashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('plans'); // 'plans', 'subscriptions', 'users'
  const [plans, setPlans] = useState([]);
  const [usersWithSubscriptions, setUsersWithSubscriptions] = useState([]); // Para listar usuários e suas assinaturas
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isSavingPlan, setIsSavingPlan] = useState(false);

  // NOVO: Adicionar este log para confirmar que o componente está sendo renderizado
  console.log("DEBUG (AdminDashboard): Componente AdminDashboard está sendo renderizado!"); 

  // Verificação de permissão para este dashboard
  const isAdminOrChampionshipManager = useMemo(() => {
    if (authLoading || !user) {
        console.log(`DEBUG (AdminDashboard Permission): Auth Loading: ${authLoading}, User: ${user ? 'exists' : 'null'}`); // DEBUG
        return false;
    }
    // Admins têm acesso total
    if (user.role === 'Admin') {
        console.log('DEBUG (AdminDashboard Permission): User is Admin. Access granted.'); // DEBUG
        return true;
    }
    // Para gestores, depende da sua regra específica.
    // Por enquanto, o acesso global a PLANOS/ASSINATURAS é APENAS para Admin.
    // Gestores podem gerenciar *seus* campeonatos, mas não este painel global.
    console.log(`DEBUG (AdminDashboard Permission): User role "${user.role}" is not Admin. Access denied.`); // DEBUG
    return false; 
  }, [user, authLoading]);

  // Função para buscar planos
  const fetchPlans = useCallback(async () => {
    try {
      setLoadingData(true);
      setError(null);
      // Seu service já busca apenas os disponíveis por padrão, mas Admin pode querer ver todos
      // Precisamos de uma rota para Admin ver TODOS os planos, inclusive inativos
      // Por enquanto, vamos usar o getAllPlans que busca os disponíveis
      const fetchedPlans = await planService.getAllPlans(); 
      setPlans(fetchedPlans.data);
      console.log('DEBUG (AdminDashboard): Plans fetched:', fetchedPlans.data); // DEBUG
    } catch (err) {
      console.error('DEBUG (AdminDashboard): Erro ao buscar planos:', err); // DEBUG
      setError('Falha ao carregar planos.');
    } finally {
      setLoadingData(false);
    }
  }, []);

  // Função para buscar usuários com assinaturas
  const fetchUsersWithSubscriptions = useCallback(async () => {
    try {
      setLoadingData(true);
      setError(null);
      // Aqui você precisaria de um endpoint no backend que retorne *todas* as assinaturas populadas com usuário e plano.
      // Vamos precisar criar esse endpoint e um service no frontend.
      // Por enquanto, vamos deixar um placeholder ou simular.
      // Ex: const allSubscriptions = await subscriptionService.getAllSubscriptions(); 
      // setUsersWithSubscriptions(allSubscriptions.data);
      console.warn('DEBUG (AdminDashboard): fetchUsersWithSubscriptions - Endpoint ainda não implementado.'); // DEBUG
      setUsersWithSubscriptions([]); // Simular vazio
    } catch (err) {
      console.error('DEBUG (AdminDashboard): Erro ao buscar assinaturas de usuários:', err); // DEBUG
      setError('Falha ao carregar assinaturas dos usuários.');
    } finally {
      setLoadingData(false);
    }
  }, []);


  useEffect(() => {
    // Redirecionamento se não for Admin, mas SÓ DEPOIS QUE O AUTH LOADING TERMINAR
    if (!authLoading && !isAdminOrChampionshipManager) {
      console.log('DEBUG (AdminDashboard useEffect): User not authorized for Admin Dashboard. Redirecting.'); // DEBUG
      navigate('/dashboard'); 
      return; // Aborta o resto do useEffect
    }

    // Se é autorizado, então carregue os dados da aba ativa
    if (isAdminOrChampionshipManager) {
        if (activeTab === 'plans') {
            fetchPlans();
        } else if (activeTab === 'subscriptions') {
            fetchUsersWithSubscriptions();
        }
    }
  }, [isAdminOrChampionshipManager, activeTab, fetchPlans, fetchUsersWithSubscriptions, navigate, authLoading]);


  // Handler para salvar/criar plano
  const handleSavePlan = async (planId, formData) => {
    setIsSavingPlan(true);
    setMessage({ text: '', type: '' });
    try {
      if (planId) {
        await planService.updatePlan(planId, formData);
        setMessage({ text: 'Plano atualizado com sucesso!', type: 'success' });
      } else {
        await planService.createPlan(formData);
        setMessage({ text: 'Plano criado com sucesso!', type: 'success' });
      }
      setShowPlanForm(false);
      setEditingPlan(null);
      fetchPlans(); // Recarrega a lista de planos
    } catch (err) {
      console.error('DEBUG (AdminDashboard): Erro ao salvar plano:', err); // DEBUG
      setMessage({ text: err.message || 'Erro ao salvar plano.', type: 'error' });
    } finally {
      setIsSavingPlan(false);
    }
  };

  // Handler para deletar plano
  const handleDeletePlan = async (planId, planName) => {
    if (!window.confirm(`Tem certeza que deseja deletar o plano "${planName}"? Esta ação é irreversível.`)) return;
    setMessage({ text: '', type: '' });
    try {
      await planService.deletePlan(planId);
      setMessage({ text: 'Plano deletado com sucesso!', type: 'success' });
      fetchPlans();
    } catch (err) {
      console.error('DEBUG (AdminDashboard): Erro ao deletar plano:', err); // DEBUG
      setMessage({ text: err.message || 'Erro ao deletar plano.', type: 'error' });
    }
  };


  if (authLoading) { // Se o useAuth ainda está carregando, mostre um loading genérico
    return <div className="admin-loading">Verificando permissões...</div>;
  }

  // Se authLoading terminou e o usuário NÃO É admin, mostre o acesso negado.
  // A navegação já ocorre no useEffect, mas este é um fallback visual.
  if (!isAdminOrChampionshipManager) {
    return <div className="admin-unauthorized">Você não tem permissão para acessar esta área.</div>;
  }

  // Se chegou aqui, é Admin e pode renderizar o dashboard.
  return (
    <div className="admin-dashboard-container">
      <h2>Painel Administrativo</h2>

      <div className="admin-tabs">
        <button onClick={() => setActiveTab('plans')} className={activeTab === 'plans' ? 'active' : ''}>Planos</button>
        <button onClick={() => setActiveTab('subscriptions')} className={activeTab === 'subscriptions' ? 'active' : ''}>Assinaturas</button>
      </div>

      {message.text && <div className={`admin-message ${message.type}`}>{message.text}</div>}

      {loadingData ? (
        <div className="admin-loading">Carregando dados da seção...</div>
      ) : (
        <>
          {activeTab === 'plans' && (
            <div className="admin-section">
              <h3>Gerenciar Planos</h3>
              {!showPlanForm && (
                <button onClick={() => setShowPlanForm(true)} className="btn-add-new-plan">Adicionar Novo Plano</button>
              )}

              {showPlanForm && (
                <PlanFormCard 
                  plan={editingPlan} 
                  onSave={handleSavePlan} 
                  onCancel={() => {setShowPlanForm(false); setEditingPlan(null);}} 
                  isSaving={isSavingPlan} 
                />
              )}

              <div className="plans-list">
                {plans.length === 0 ? (
                  <p>Nenhum plano cadastrado.</p>
                ) : (
                  <ul>
                    {plans.map(plan => (
                      <li key={plan._id} className="plan-item">
                        <span>{plan.name} - R$ {plan.price.toFixed(2).replace('.', ',')} / {plan.interval}</span>
                        <div className="plan-actions">
                          <button onClick={() => {setEditingPlan(plan); setShowPlanForm(true);}} className="btn-edit-plan">Editar</button>
                          <button onClick={() => handleDeletePlan(plan._id, plan.name)} className="btn-delete-plan">Deletar</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="admin-section">
              <h3>Assinaturas de Usuários</h3>
              {usersWithSubscriptions.length === 0 ? (
                <p>Nenhum usuário com assinatura ativa ou pendente (ou endpoint não implementado).</p>
              ) : (
                <ul>
                  {usersWithSubscriptions.map(sub => (
                    <li key={sub._id} className="subscription-item">
                      <span>Usuário: {sub.user?.name || 'N/A'} - Plano: {sub.plan?.name || 'N/A'} - Status: {sub.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminDashboard;